import { useEffect, useMemo, useState } from "react";
import { Box, Container, InputBase, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";
import type { GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { YouTubeChannel } from "./youtubechannel.types";
import YouTubeChannelDataGrid from "./components/YoutubeChannelDataGrid";
import { fetchYouTubeChannels } from "./services/youtubeChannelService";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

export default function YouTubeChannelListPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const loadChannels = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchYouTubeChannels();
        setChannels(response.data.results || []);
      } catch (err: unknown) {
        console.error("Failed to load YouTube channels", err);
        setError(
          (err as { message?: string })?.message ||
            "Unable to load YouTube channels.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadChannels();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.toLowerCase().trim();

    if (!term) return channels;

    return channels.filter((channel) => {
      return (
        channel.name.toLowerCase().includes(term) ||
        channel.display_name?.toLowerCase().includes(term) ||
        channel.company.name.toLowerCase().includes(term)
      );
    });
  }, [channels, search]);

  const handleView = (channel: YouTubeChannel) => {
    navigate(`/youtube-channels/${channel.id}`);
  };

  const handleReauthenticate = (channel: YouTubeChannel) => {
    if (channel.reauth_url) {
      window.location.href = channel.reauth_url;
    }
  };

  const handleRowClick = (params: GridRowParams<YouTubeChannel>) => {
    navigate(`/youtube-channels/${params.row.id}`);
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          mb: 3,
          width: { xs: "100%", md: "35%" },
          display: "flex",
          alignItems: "center",
          backgroundColor: "background.paper",
          borderRadius: 2,
          px: 1.5,
          py: 0.5,
        }}
      >
        <SearchOutlinedIcon sx={{ color: "text.primary", mr: 1 }} />
        <InputBase
          fullWidth
          placeholder="Search by YouTube channel name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ color: "text.primary" }}
        />
      </Box>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <YouTubeChannelDataGrid
        rows={filteredRows}
        rowCount={filteredRows.length}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onView={handleView}
        onReauthenticate={handleReauthenticate}
        onRowClick={handleRowClick}
        canReauthenticate={canEdit}
      />
    </Container>
  );
}
